/**
 * DOM bet grid + chip picker + BATAL / ULANG / PASANG buttons. Renders any
 * variant's layout via the BetGridLayout descriptor (see `variants.ts`).
 *
 * Single host element drives all visible bet cells — Pixi/Three.js sit beneath.
 */
import type {
  BallVariant,
  BetGridCell,
  GameConfig,
} from '../types/game';

export interface BetPanelHandlers {
  onBet: (betKey: string) => void;
  onSelectChip: (index: number) => void;
  onBatal: () => void;
  onUlang: () => void;
  onPasang: () => void;
}

export class BetPanel {
  private readonly grid: HTMLElement;
  private readonly chipBar: HTMLElement;
  private readonly batalBtn: HTMLButtonElement;
  private readonly ulangBtn: HTMLButtonElement;
  private readonly pasangBtn: HTMLButtonElement;

  /** betKey → array of cells representing that bet (often one). */
  private cellsByKey: Map<string, HTMLElement[]> = new Map();
  /** betKey → stake-label nodes (one per cell). */
  private stakeLabels: Map<string, HTMLElement[]> = new Map();
  /** Chip buttons in order. */
  private chipButtons: HTMLButtonElement[] = [];

  private currentVariant: BallVariant | null = null;

  constructor(handlers: BetPanelHandlers) {
    const grid = document.getElementById('bet-grid');
    const chipBar = document.getElementById('chip-bar');
    const batal = document.getElementById('btn-batal');
    const ulang = document.getElementById('btn-ulang');
    const pasang = document.getElementById('btn-pasang');
    if (!grid || !chipBar || !batal || !ulang || !pasang) {
      throw new Error('BetPanel: required DOM nodes missing');
    }
    this.grid = grid;
    this.chipBar = chipBar;
    this.batalBtn = batal as HTMLButtonElement;
    this.ulangBtn = ulang as HTMLButtonElement;
    this.pasangBtn = pasang as HTMLButtonElement;

    this.batalBtn.addEventListener('click', () => handlers.onBatal());
    this.ulangBtn.addEventListener('click', () => handlers.onUlang());
    this.pasangBtn.addEventListener('click', () => handlers.onPasang());

    this.grid.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>('[data-bet-key]');
      if (!target) return;
      const key = target.dataset.betKey;
      if (key) handlers.onBet(key);
    });

    this.handlers = handlers;
  }

  private handlers: BetPanelHandlers;

  /**
   * Both single-player and multiplayer use the BATAL/ULANG/PASANG bar. In MP
   * the bets are staged locally and submitted as a batch on PASANG, so all
   * three buttons are visible. Kept as a method for API compatibility.
   */
  setMode(_mode: 'single' | 'multi'): void {
    // both modes show all three buttons — see method docstring
  }

  renderVariant(variant: BallVariant): void {
    this.currentVariant = variant;
    this.cellsByKey.clear();
    this.stakeLabels.clear();
    this.grid.innerHTML = '';
    this.grid.style.gridTemplateColumns = variant.layout.columnsTemplate;
    this.grid.style.gridAutoRows = 'minmax(28px, auto)';

    for (const row of variant.layout.rows) {
      this.renderRow(variant, row.cells);
    }
  }

  private renderRow(variant: BallVariant, cells: BetGridCell[]): void {
    for (const cell of cells) {
      const el = document.createElement('div');
      el.className = 'bet-cell';

      if (cell.betKey) {
        const def = variant.bets.find((b) => b.key === cell.betKey);
        const colour = cell.colorOverride ?? def?.color ?? 'dark';
        el.classList.add(colour);
        el.dataset.betKey = cell.betKey;

        const label = document.createElement('span');
        label.textContent = cell.text ?? def?.label ?? cell.betKey;
        el.appendChild(label);

        const stake = document.createElement('span');
        stake.className = 'stake';
        el.appendChild(stake);

        addToMap(this.cellsByKey, cell.betKey, el);
        addToMap(this.stakeLabels, cell.betKey, stake);
      } else {
        // Static label cell.
        el.classList.add(cell.colorOverride ?? 'dark');
        el.style.pointerEvents = 'none';
        const label = document.createElement('span');
        label.textContent = cell.text ?? '';
        el.appendChild(label);
      }

      if (cell.span && cell.span > 1) {
        el.style.gridColumn = `span ${cell.span}`;
      }
      this.grid.appendChild(el);
    }
  }

  bindChips(config: GameConfig): void {
    this.chipBar.innerHTML = '';
    this.chipButtons = [];
    config.chipValues.forEach((v, i) => {
      const btn = document.createElement('button');
      btn.className = 'chip-pick';
      btn.textContent = shortChip(v);
      btn.addEventListener('click', () => this.handlers.onSelectChip(i));
      this.chipBar.appendChild(btn);
      this.chipButtons.push(btn);
    });
  }

  setSelectedChip(index: number): void {
    this.chipButtons.forEach((b, i) => b.classList.toggle('active', i === index));
  }

  setStake(betKey: string, amount: number): void {
    const labels = this.stakeLabels.get(betKey);
    if (!labels) return;
    const text = amount > 0 ? formatAmt(amount) : '';
    for (const l of labels) l.textContent = text;
  }

  clearAllStakes(): void {
    for (const list of this.stakeLabels.values()) {
      for (const l of list) l.textContent = '';
    }
  }

  setBettingEnabled(on: boolean): void {
    if (!this.currentVariant) return;
    for (const list of this.cellsByKey.values()) {
      for (const el of list) el.classList.toggle('disabled', !on);
    }
    this.pasangBtn.disabled = !on;
    this.batalBtn.disabled = !on;
    this.ulangBtn.disabled = !on;
  }

  /**
   * Mark winning cells.
   *
   * Live Ball bets are pure functions of the drawn ball number (straights,
   * color groups, ranges, halves, sectors, ganjil/genap), so we compute the
   * highlight set locally from each bet's `winsOn`. We deliberately do NOT
   * trust the server's `@<winId>` suffix list because it has been observed
   * to omit valid wins (e.g. color group on BAL12M). The server's balance /
   * WinLost numbers remain authoritative for payouts — only the visual
   * highlight is computed client-side.
   */
  highlightWinners(winningBallNumber: number, _winningBetIds?: number[]): void {
    if (!this.currentVariant) return;
    this.clearHighlights();
    for (const def of this.currentVariant.bets) {
      if (!def.winsOn.includes(winningBallNumber)) continue;
      for (const el of this.cellsByKey.get(def.key) ?? []) {
        el.classList.add('winning');
      }
    }
  }

  clearHighlights(): void {
    for (const list of this.cellsByKey.values()) {
      for (const el of list) el.classList.remove('winning');
    }
  }
}

function shortChip(v: number): string {
  if (v >= 1_000_000) return `${v / 1_000_000}M`;
  if (v >= 1_000) return `${v / 1_000}K`;
  return String(v);
}
function formatAmt(v: number): string {
  return Math.round(v).toLocaleString('en-US');
}

function addToMap<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}
